import { useQuery } from '@tanstack/react-query';
import useDebounce from 'hooks/useDebounce';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import SearchableField from './SearchableField';
import {
  City,
  ISearchableFieldContainer,
  State,
  SuggestionOption,
} from './types';

const SearchableFieldContainer: FC<ISearchableFieldContainer> = ({
  name,
  label,
  value,
  queryKey,
  queryFunction,
  onChange,
  ...props
}) => {
  const initialState: State = {
    searchInputValue: value?.name || '',
    valueSelected: false,
    showDropDown: false,
    suggestionOptions: undefined,
    noResultsFound: false,
    isLoading: false,
  };
  const [state, setState] = useState<State>(initialState);

  const generateKey = () => {
    return queryKey ? queryKey : name;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value: searchInputValue } = e.target;
    setState(prevState => ({
      ...prevState,
      showDropDown: true,
      valueSelected: false,
      isLoading: true,
      searchInputValue,
    }));

    onChange({
      id: 0,
      name: searchInputValue,
    });
  };

  const handleSelect = (option: SuggestionOption) => {
    setState(prevState => ({
      ...prevState,
      showDropDown: false,
      valueSelected: true,
    }));

    onChange(option);
  };

  const handleOnReset = () => {
    setState(prevState => ({
      ...prevState,
      valueSelected: false,
      searchInputValue: '',
      noResultsFound: false,
      isLoading: false,
    }));
  };
  // debounce the search value to avoid unnecessary api calls
  const searchTerm = useDebounce(value?.name || '', 200);

  // fetch the search suggestion list
  const { data } = useQuery<City[]>(
    [generateKey(), searchTerm],
    () => queryFunction(searchTerm),
    {
      staleTime: 1000 * 60, // 1 minute
    }
  );

  //sync the data state with the current context state
  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      suggestionOptions: data,
      noResultsFound: data?.length === 0,
      isLoading: false, 
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <SearchableField
      label={label}
      name={name}
      state={state}
      setState={setState}
      onChange={handleChange}
      onSelect={handleSelect}
      onReset={handleOnReset}
      value={value?.name || ''}
      {...props}
    />
  );
};

export default SearchableFieldContainer;
